import React, { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import Meta from 'src/components/Meta';
import { useGaEventPageView } from 'src/utils/gaEvent';
import styled from 'styled-components';
import {
  DataTitle,
  DataValue,
  NoData,
  PageCard,
  PageContainer,
  PageFrameworkLibSkeleton,
  Poppins,
  Spacer,
  TitleWithBack,
} from 'src/common';
import ExpandCollapse from 'src/common/ExpandCollapse';
import { useNavigate, useParams } from 'react-router';
import { ScenarioLibraryTableSection } from './comps';
import { TableIds } from 'src/api/types/misc';
import { ScenarioLibraryItem } from 'src/components/GenericTable/sharedColumns';
import { ScenarioGroupWithDetails, useScenarioGroupsWithScenarios } from 'src/graphql';
import { isQueryLoading } from 'src/graphql/client';
import { useAuth } from 'src/state/auth';
import Button from 'src/components/form/Button';
import useWorkspaceVariables from 'src/hooks/useWorkspaceVariables';
import { downloadScenarioGroupCsv } from './export';
import { ScenarioGroupImportModal } from './ScenarioGroupImportModal';
import {
  DELETE_SCENARIO_GROUP_RISKS,
  DeleteScenarioGroupRisksMutation,
  DeleteScenarioGroupRisksMutationVariables,
} from 'src/graphql/contentImport';
import { showErrorToast, showSuccessToast } from 'src/components/Toast';

const Div = styled(PageContainer)`
  .content {
    display: grid;
    grid-gap: 20px;

    &__row {
      display: grid;
      grid-template-columns: 2fr 1fr 1fr;
      grid-gap: 10px;
      align-items: center;
    }
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .content {
      grid-gap: 10px;
      &__row {
        grid-template-columns: 1fr;
      }
    }
  }
`;

const toLibraryItem = (
  scenario: ScenarioGroupWithDetails['scenarios'][number],
  author?: string | null,
  groupName?: string | null,
): ScenarioLibraryItem => {
  const frequencyTimes =
    scenario.baseRisk?.frequencyTimes ??
    scenario.projectedRisk?.frequencyTimes ??
    scenario.liveRisk?.frequencyTimes ??
    null;

  const frequencyYears =
    scenario.baseRisk?.frequencyYears ??
    scenario.projectedRisk?.frequencyYears ??
    scenario.liveRisk?.frequencyYears ??
    null;

  const expectedLoss =
    scenario.baseRisk?.expectedLoss ?? scenario.projectedRisk?.expectedLoss ?? scenario.liveRisk?.expectedLoss ?? null;

  return {
    id: scenario.id,
    name: scenario.name,
    author: author ?? scenario.ownerName ?? undefined,
    groupName: groupName ?? undefined,
    description:
      scenario.description ||
      scenario.managementNote ||
      scenario.riskNarrative ||
      scenario.consequenceNote ||
      undefined,
    source: scenario.source,
    event: scenario.event,
    consequence: scenario.consequence,
    riskNarrative: scenario.riskNarrative,
    frequencyTimes,
    frequencyYears,
    frequencyNote: scenario.frequencyNote ?? undefined,
    expectedLoss: expectedLoss ?? undefined,
    managementStrategy: scenario.managementStrategy ?? undefined,
    lowerFormula: scenario.lowerFormula ?? undefined,
    upperFormula: scenario.upperFormula ?? undefined,
    note: scenario.consequenceNote ?? scenario.managementNote ?? undefined,
    assessmentId: scenario.assessment?.id ?? undefined,
  };
};

const ScenarioGroupPage = () => {
  const { scenarioGroupId } = useParams<{ scenarioGroupId: string }>();
  const { workspaceId, isPlatformAdmin } = useAuth();
  const [isImportOpen, setIsImportOpen] = useState(false);
  const { variables: workspaceVariables, isLoading: isWorkspaceVariablesLoading } = useWorkspaceVariables();
  const navigate = useNavigate();

  const { data, loading, networkStatus, refetch } = useScenarioGroupsWithScenarios(workspaceId);
  const [deleteScenarioGroupRisks, { loading: isDeletingRisks }] = useMutation<
    DeleteScenarioGroupRisksMutation,
    DeleteScenarioGroupRisksMutationVariables
  >(DELETE_SCENARIO_GROUP_RISKS);
  const isLoading = isQueryLoading({ loading, data, networkStatus });

  const group = data?.scenarioGroups.find((el) => el.id === scenarioGroupId);

  const tableData = useMemo(
    () => group?.scenarios?.map((scenario) => toLibraryItem(scenario, group?.author, group?.name)) ?? [],
    [group],
  );
  const canManageContent = isPlatformAdmin;

  useGaEventPageView(`Cydea | Scenario Group | ${group?.name || ''}`);

  return (
    <>
      <Meta title={`Cydea | Scenario Group${group?.name ? ` | ${group.name}` : ''}`} feedbackScript />
      {!isLoading ? (
        <Div data-cy="scenario-group-page">
          {group ? (
            <>
              <PageCard>
                <TitleWithBack title="SCENARIO GROUP" gradient />
                <Spacer $px={30} />
                {group.name ? (
                  <Poppins px={32} weight={600}>
                    {group.name}
                  </Poppins>
                ) : (
                  <NoData />
                )}
                <Spacer $px={44} />

                <div className="content">
                  <div className="content__row">
                    <div>
                      <DataTitle>FULL NAME</DataTitle>
                      {group.name ? <DataValue>{group.name}</DataValue> : <NoData />}
                    </div>
                    <div>
                      <DataTitle>AUTHOR</DataTitle>
                      {group.author ? <DataValue>{group.author}</DataValue> : <NoData />}
                    </div>
                  </div>
                  <div>
                    <DataTitle>DESCRIPTION</DataTitle>
                    {group.description ? <ExpandCollapse text={group.description} /> : <NoData />}
                  </div>
                </div>
              </PageCard>
              <Spacer $px={30} />
              {canManageContent && (
                <div className="actions">
                  <Button
                    secondary
                    onClick={() => setIsImportOpen(true)}
                    $constWidth={190}
                    data-cy="import-scenario-group-csv"
                  >
                    IMPORT CSV
                  </Button>
                  <Button
                    secondary
                    disabled={isWorkspaceVariablesLoading}
                    onClick={() =>
                      downloadScenarioGroupCsv({
                        group,
                        workspaceVariables,
                      })
                    }
                    $constWidth={190}
                    data-cy="export-scenario-group-csv"
                  >
                    EXPORT CSV
                  </Button>
                </div>
              )}
              {group && canManageContent && (
                <ScenarioGroupImportModal
                  isOpen={isImportOpen}
                  onClose={() => setIsImportOpen(false)}
                  onImported={async (summary) => {
                    // Scenario pack imports create a new version, so follow the current version returned by the API.
                    const nextGroupId = summary.groupId;
                    await refetch();
                    if (nextGroupId && nextGroupId !== group.id) {
                      navigate(`/scenario-library/groups/${nextGroupId}`, { replace: true });
                    }
                  }}
                  scenarioGroupId={group.id}
                  groupName={group.name}
                  existingScenarioIds={group.scenarios.map((scenario) => scenario.id)}
                  workspaceVariables={workspaceVariables}
                />
              )}
              <Spacer $px={30} />
              <ScenarioLibraryTableSection
                data={tableData}
                isLoading={false}
                tableId={TableIds.scenarioLibGroup}
                canDelete={canManageContent}
                isDeleting={isDeletingRisks}
                onDeleteScenarios={async (scenarioIds) => {
                  if (!group) return;

                  try {
                    const result = await deleteScenarioGroupRisks({
                      variables: {
                        scenarioGroupId: group.id,
                        scenarioIds,
                      },
                    });
                    const nextGroupId = result.data?.deleteScenarioGroupRisks.groupId;
                    showSuccessToast({ title: 'Scenario pack version created' });
                    await refetch();
                    if (nextGroupId && nextGroupId !== group.id) {
                      navigate(`/scenario-library/groups/${nextGroupId}`, { replace: true });
                    }
                  } catch (error: any) {
                    showErrorToast(error?.message || 'Could not delete selected risks.');
                    throw error;
                  }
                }}
              />
            </>
          ) : (
            <NoData />
          )}
        </Div>
      ) : (
        <Div>
          <PageFrameworkLibSkeleton />
        </Div>
      )}
    </>
  );
};

export default ScenarioGroupPage;
